/**
 * ============================================
 * USE PACIENTES - Hooks para gestión de pacientes
 * ============================================
 *
 * Hooks de TanStack Query para:
 * - Listar pacientes (useQuery con paginación)
 * - Crear paciente (useMutation)
 * - Actualizar paciente (useMutation)
 * - Eliminar paciente (useMutation)
 * - Activar/Desactivar paciente (useMutation)
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import {
  getPacientes,
  getPaciente,
  createPaciente,
  updatePaciente,
  deletePaciente,
  activatePaciente,
  deactivatePaciente,
  buscarPacientes,
  type PacienteCreateData,
  type PacienteUpdateData,
  type PacienteFilters,
} from "@/services/pacientes";

// Re-export functions for useServerDataTable
export { getPacientes } from "@/services/pacientes";

// Re-export types
export type {
  Paciente,
  PacienteListItem,
  PacienteCreateData,
  PacienteUpdateData,
  PacienteFilters,
  TipoDocumento,
  Sexo,
  Edad,
} from "@/services/pacientes";

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Hook para cargar la lista paginada de pacientes
 */
export function usePacientes(filters?: PacienteFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.pacientes.list(filters || {}),
    queryFn: () => getPacientes(filters),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook para cargar un paciente por ID
 */
export function usePaciente(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.pacientes.detail(id),
    queryFn: () => getPaciente(id),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook para buscar pacientes
 */
export function useBuscarPacientes(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.pacientes.search(query),
    queryFn: () => buscarPacientes(query),
    enabled: (options?.enabled ?? true) && query.length >= 2,
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

interface MutationCallbacks {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface CreateMutationCallbacks {
  onSuccess?: (paciente: import("@/services/pacientes").Paciente) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook para crear un nuevo paciente
 */
export function useCreatePaciente(callbacks?: CreateMutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PacienteCreateData) => createPaciente(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pacientes.all });
      callbacks?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para actualizar un paciente
 */
export function useUpdatePaciente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: PacienteUpdateData }) =>
      updatePaciente(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pacientes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para eliminar un paciente (hard delete)
 */
export function useDeletePaciente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deletePaciente(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pacientes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para activar un paciente
 */
export function useActivatePaciente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => activatePaciente(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pacientes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para desactivar un paciente (soft delete)
 */
export function useDeactivatePaciente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deactivatePaciente(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.pacientes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}
